import { Input, Segment, Text } from "@fluentui/react-northstar";
import { CouponDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

export const PerUserPricingCalculator: React.FC<{
  perUserPrice: number;
  defaultUsers: number;
  taxPercentage: number;
  coupon: CouponDTO | undefined;
}> = ({ taxPercentage, perUserPrice, defaultUsers, coupon }) => {
  const [calculatedTotal, setCalculatedTotal] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [discount, setDiscount] = useState<number>();
  const [, setTax] = useState<number>(0);
  const [users, setUsers] = useState(defaultUsers);

  const numFormat = getCurrencyFormat();
  let finePrint = `* Totals are an estimate and you will be charged on actual usage`;

  useEffect(() => {
    const baseTotal = users * perUserPrice;
    setCalculatedTotal(baseTotal);

    const t = taxPercentage ? total * (taxPercentage / 100) : 0;
    setTax(t);

    if (coupon) {
      const beforeTotal = baseTotal + t;

      let discount: number | undefined;
      if (coupon.percentOff) {
        discount = beforeTotal * (coupon.percentOff * 0.01);
      } else if (coupon.amountOff) {
        discount = coupon.amountOff * 0.01;
      }

      if (discount) {
        setDiscount(discount);
        setTotal(beforeTotal - discount);
      }
    } else {
      setTotal(baseTotal + t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPercentage, setTotal, setTax, setCalculatedTotal, users, coupon]);

  return (
    <>
      <Text content="Pricing Calculator" weight="bold" size="large" />
      <Segment styles={{ marginTop: ".75rem" }} color="brand">
        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td style={{ width: "100%" }}>
                <Input
                  type="number"
                  min={1}
                  variables={{ width: "30px" }}
                  max={500}
                  value={users}
                  onChange={(_e, d) => {
                    if (d?.value) {
                      const v = parseInt(d.value);
                      if (!isNaN(v)) {
                        setUsers(v);
                      }
                    }
                  }}
                />{" "}
                users x {numFormat.format(perUserPrice)}
              </td>
              <td align="right">{numFormat.format(calculatedTotal)}</td>
            </tr>

            {coupon && discount && (
              <tr>
                <td>
                  Coupon <b>{coupon.id}</b>: (
                  <>
                    {coupon.percentOff && <Text content={`${coupon.percentOff}% Off`} />}
                    {coupon.amountOff && (
                      <Text content={`${numFormat.format(coupon.amountOff * 0.01)} Off`} />
                    )}
                  </>
                  )
                </td>
                <td align="right">{numFormat.format(discount * -1)}</td>
              </tr>
            )}
            <tr>
              <td>Tax:</td>
              <td align="right">?</td>
            </tr>
            <tr>
              <td>*Total:</td>
              <td align="right">{numFormat.format(total)}</td>
            </tr>
          </tbody>
        </table>
      </Segment>

      <Text content={finePrint} weight="light" as="div" styles={{ marginTop: ".5rem" }} />
    </>
  );
};

const getCurrencyFormat = () => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
};
